import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";

export const TEETH_UPPER = [
  "UR8", "UR7", "UR6", "UR5", "UR4", "UR3", "UR2", "UR1",
  "UL1", "UL2", "UL3", "UL4", "UL5", "UL6", "UL7", "UL8",
];

export const TEETH_LOWER = [
  "LR8", "LR7", "LR6", "LR5", "LR4", "LR3", "LR2", "LR1",
  "LL1", "LL2", "LL3", "LL4", "LL5", "LL6", "LL7", "LL8",
];

export default function DentalAirwaySection({
  expanded = false,
  onToggleExpand,
  dentalMarks = {},
  onToothClick,
  dentures = false,
  onDenturesChange,
  patientInformed = false,
  onPatientInformedChange,
}) {
  return (
    <Accordion
      expanded={Boolean(expanded)}
      onChange={onToggleExpand}
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: "10px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#0284c7" }} />}
        sx={{ bgcolor: "#f8fafc", px: 2, py: 0.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <HealthAndSafetyIcon sx={{ color: "#0284c7", fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1e293b" }}>
            3.2 Dental Assessment & 32-Tooth Interactive Chart
          </Typography>
          {dentures && <Chip size="small" label="Dentures" sx={{ height: 20, fontSize: "0.7rem" }} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2.5, bgcolor: "#fff" }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Click individual tooth to cycle status: <strong>Normal (White)</strong> ➔{" "}
          <strong>Missing [*] (Red)</strong> ➔ <strong>Loose [O] (Amber)</strong> ➔ Normal.
        </Typography>

        {/* Upper Arch */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
          Maxillary Arch (Upper)
        </Typography>
        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ my: 1, overflowX: "auto" }}>
          {TEETH_UPPER.map((t) => {
            const st = dentalMarks[t] || "NORMAL";
            return (
              <Paper
                key={t}
                onClick={() => onToothClick && onToothClick(t)}
                elevation={0}
                sx={{
                  width: 34,
                  height: 38,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  border: "1px solid #cbd5e1",
                  bgcolor: st === "MISSING" ? "#fee2e2" : st === "LOOSE" ? "#fef3c7" : "#fff",
                  color: st === "MISSING" ? "#991b1b" : st === "LOOSE" ? "#92400e" : "#0f172a",
                  "&:hover": { borderColor: "#0284c7" },
                }}
              >
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700 }}>{t}</Typography>
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 800 }}>
                  {st === "MISSING" ? "*" : st === "LOOSE" ? "O" : ""}
                </Typography>
              </Paper>
            );
          })}
        </Stack>

        {/* Lower Arch */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
          Mandibular Arch (Lower)
        </Typography>
        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ my: 1, overflowX: "auto" }}>
          {TEETH_LOWER.map((t) => {
            const st = dentalMarks[t] || "NORMAL";
            return (
              <Paper
                key={t}
                onClick={() => onToothClick && onToothClick(t)}
                elevation={0}
                sx={{
                  width: 34,
                  height: 38,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  border: "1px solid #cbd5e1",
                  bgcolor: st === "MISSING" ? "#fee2e2" : st === "LOOSE" ? "#fef3c7" : "#fff",
                  color: st === "MISSING" ? "#991b1b" : st === "LOOSE" ? "#92400e" : "#0f172a",
                  "&:hover": { borderColor: "#0284c7" },
                }}
              >
                <Typography sx={{ fontSize: "0.65rem", fontWeight: 700 }}>{t}</Typography>
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 800 }}>
                  {st === "MISSING" ? "*" : st === "LOOSE" ? "O" : ""}
                </Typography>
              </Paper>
            );
          })}
        </Stack>

        <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dentures}
                onChange={(e) => onDenturesChange && onDenturesChange(e.target.checked)}
              />
            }
            label="Removable Dentures / Bridges / Crowns Present"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={patientInformed}
                onChange={(e) =>
                  onPatientInformedChange && onPatientInformedChange(e.target.checked)
                }
              />
            }
            label="Patient Explicitly Counseled on Risks of Dental Injury"
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
